import type { Theme } from '@jgp-er-dev/themes'
import { DataValueType } from 'highcharts'
import { toBlob } from 'html-to-image'
import pptxgen from 'pptxgenjs'
import zipcelx from 'zipcelx'

import Highcharts from '../highcharts'
import {
  CUSTOM_CHART_TITLE_CLASS,
  HIGHCHARTS_CONTAINER_CLASS,
  HIGHCHARTS_EXPORTING_GROUP_CLASS,
  HIGHCHARTS_PLOT_BACKGROUND_CLASS,
  HIGHCHARTS_RANGE_SELECTOR_GROUP_CLASS,
  HIGHCHARTS_STOCKTOOLS_WRAPPER_CLASS,
  HIGHCHARTS_TITLE_CLASS,
  MAIN_PAGE_TITLE,
  SLIDE_PAGE_TITLE,
} from './constants'
import {
  getMainPageMasterProps,
  getPlaceholder,
  getSlidePageMasterProps,
} from './slides'

export const addImageToClipboard = async (container: HTMLElement) => {
  const CLASSES_TO_REMOVE = [
    HIGHCHARTS_RANGE_SELECTOR_GROUP_CLASS,
    HIGHCHARTS_EXPORTING_GROUP_CLASS,
    HIGHCHARTS_STOCKTOOLS_WRAPPER_CLASS,
  ]
  const TITLE_CHART_DIST_PX = 10

  const renderContainer = document.createElement('div')
  const height = container.clientHeight
  const width = container.clientWidth

  renderContainer.style.height = height.toString()
  renderContainer.style.width = width.toString()
  renderContainer.style.position = 'fixed'
  renderContainer.style.top = '0'
  renderContainer.style.transform = 'translateY(-100%)'

  document.body.appendChild(renderContainer)

  const copy = container.cloneNode(true) as HTMLDivElement

  const customTitleElement = container.querySelector<HTMLElement>(
    '.' + CUSTOM_CHART_TITLE_CLASS
  )

  const highchartsTitleElement = container.querySelector<HTMLElement>(
    '.' + HIGHCHARTS_TITLE_CLASS
  )

  const plotBackgroundElement = container.querySelector<HTMLElement>(
    '.' + HIGHCHARTS_PLOT_BACKGROUND_CLASS
  )

  const copyChartElement =
    (copy.matches('.' + HIGHCHARTS_CONTAINER_CLASS) && copy) ||
    copy.querySelector<HTMLElement>('.' + HIGHCHARTS_CONTAINER_CLASS)

  const copyHighchartsTitleElement = copy.querySelector<HTMLElement>(
    '.' + HIGHCHARTS_TITLE_CLASS
  )

  const titleRect = (
    customTitleElement ?? highchartsTitleElement
  )?.getClientRects()[0]
  const plotRect = plotBackgroundElement?.getClientRects()[0]

  const removeHeight =
    titleRect && plotRect
      ? Math.max(0, plotRect.top - titleRect.bottom - TITLE_CHART_DIST_PX)
      : 0

  if (customTitleElement) {
    copyChartElement!.style.transform = `translateY(-${removeHeight}px)`
  } else {
    copyHighchartsTitleElement!.style.transform = `translateY(${removeHeight}px)`
    copy.style.transform = `translateY(-${removeHeight}px)`
  }

  renderContainer.appendChild(copy)

  await toBlob(copy, {
    backgroundColor: 'white',
    height: height - removeHeight,
    filter: (node) =>
      !CLASSES_TO_REMOVE.some((className) =>
        node.classList?.contains(className)
      ),
  })
    .then((blob) =>
      (navigator.clipboard as any).write([
        new ClipboardItem({ 'image/png': blob! }),
      ])
    )
    .finally(() => renderContainer.remove())
}

export const addChartIdToClipboard = async (reportManagerId: string) => {
  ;(navigator.clipboard as any).writeText(reportManagerId)
}

export const getChartSVG = (chart: Highcharts.Chart) =>
  'data:image/svg+xml;base64,' +
  btoa(
    unescape(
      encodeURIComponent(
        (chart as any).getSVGForExport(chart.options.exporting?.chartOptions)
      )
    )
  )

const getImageDimensions = async (
  image: string
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('Failed to get image dimensions'))

    img.src = image
  })

const insertScaledImageToSlide = (args: {
  slide: pptxgen.Slide
  x: number
  y: number
  maxHeight: number
  maxWidth: number
  dimensions: { width: number; height: number }
  data: string
  align: 'top' | 'center' | 'bottom'
}) => {
  const scaling = Math.min(
    args.maxHeight / args.dimensions.height,
    args.maxWidth / args.dimensions.width
  )

  const width = args.dimensions.width * scaling
  const height = args.dimensions.height * scaling

  args.slide.addImage({
    x: args.x,
    y:
      args.align === 'top'
        ? args.y
        : args.align === 'center'
        ? args.y + (args.maxHeight - height) / 2
        : args.y + args.maxHeight - height,
    w: width,
    h: height,
    data: args.data,
  })
}

export const genPPT = async (args: {
  svgs: string[]
  imagesPerPage: number
  theme: Theme
  title?: string
  subject?: string
  filename?: string
}) => {
  const ppt = new pptxgen()
  ppt.layout = 'LAYOUT_WIDE'
  ppt.defineSlideMaster(getMainPageMasterProps(args.theme))
  ppt.defineSlideMaster(getSlidePageMasterProps())
  const mainPageSlide = ppt.addSlide({ masterName: MAIN_PAGE_TITLE })
  mainPageSlide.addText(args.title ?? 'Untitled presentation', {
    placeholder: 'presentationTitle',
    align: 'left',
    fontSize: 68,
    fontFace: 'Founders Grotesk Semibold',
    color: 'FFFFFF',
  })

  const [companyLogoDimensions, assetLogoDimensions] = await Promise.all([
    getImageDimensions(args.theme.compass.presentation.companyLogoData),
    getImageDimensions(args.theme.compass.presentation.assetLogoData),
  ])

  insertScaledImageToSlide({
    slide: mainPageSlide,
    x: 10.17,
    y: 5.75,
    maxHeight: 1.5,
    maxWidth: 2.5,
    dimensions: companyLogoDimensions,
    data: args.theme.compass.presentation.companyLogoData,
    align: 'center',
  })

  await Promise.all(
    args.svgs.map(async (svg) => ({
      data: svg,
      ...(await getImageDimensions(svg)),
    }))
  ).then((images) => {
    let slide: pptxgen.Slide
    images.forEach((image, idx) => {
      if (idx % args.imagesPerPage === 0) {
        slide = ppt.addSlide({ masterName: SLIDE_PAGE_TITLE })
        slide.addText(args.subject ?? '', {
          placeholder: 'subject',
          align: 'right',
        })
      }
      const placeholder = getPlaceholder(
        idx % args.imagesPerPage,
        args.imagesPerPage
      )

      const scaling = Math.min(
        placeholder.w / image.width,
        placeholder.h / image.height
      )

      slide.addImage({
        data: image.data,
        x: placeholder.x,
        y: placeholder.y,
        w: image.width * scaling,
        h: image.height * scaling,
        sizing: {
          type: 'contain',
          w: placeholder.w,
          h: placeholder.h,
        },
      })

      insertScaledImageToSlide({
        slide,
        x: 0.8,
        y: 0.1,
        maxHeight: 0.35,
        maxWidth: 2.3,
        dimensions: assetLogoDimensions,
        data: args.theme.compass.presentation.assetLogoData,
        align: 'bottom',
      })
    })
  })

  await ppt.writeFile({ fileName: args.filename ?? 'Relatório' })
}

export function downloadXLSX(
  this:
    | Highcharts
    | Highcharts.EventCallbackFunction<Highcharts.Chart>
    | undefined
) {
  const rows = this.getDataRows(true)
  const data = rows.slice(1).map((row: DataValueType[]) =>
    row.map((column: DataValueType) => ({
      type: typeof column === 'number' ? 'number' : 'string',
      value: column,
    }))
  )

  const filename = this.options.exporting.filename ?? 'chart'
  zipcelx({ filename, sheet: { data } })
}
